/**
 * Renders a single PDF page
 */
import React from "react";
import ReactDOM from "react-dom";
import $ from "jquery";
import * as Utils from "../documents/utils";
import * as RenderingUtils from "../documents/rendering_utils";
import Clipboard from "clipboard";

export default class extends React.Component {
  constructor(props) {
    super(props);

    this.pdf = null;
    this.page = null;
    this.textContent = null;
    this.renderTimer = null;
    this.mounted = false;
    this.clipboardInitialized = false;

    this.state = {
      loading: true,
      renderCanvas: false
    }

  }

  /**
   * Is triggered when component is rendered on page for first time
   */
  componentDidMount() {
    // go grab the PDF
    this.mounted = true;
    this.loadPdf();
  }

  /**
   * Tests if component should be updated
   */
  shouldComponentUpdate(nextProps, nextState) {
    return nextProps.x != this.props.x ||
           nextProps.y != this.props.y ||
           nextProps.scale != this.props.scale ||
           nextProps.width != this.props.width ||
           nextProps.height != this.props.height ||
           nextProps.isFavorite != this.props.isFavorite ||
           nextState.renderCanvas != this.state.renderCanvas ||
           nextState.loading != this.state.loading;
  }

  /**
   * Is fired when a re-render is triggered
   */
  componentDidUpdate() {
    if(this.refs.share && !this.clipboardInitialized) {
      this.clipboardInitialized = true;
      var clipboard = new Clipboard(ReactDOM.findDOMNode(this.refs.share));

      clipboard.on("success", () => {
        // Flash.setMessage("info", "The link to this page was copied to your clipboard.");
      });
    }

    if(this.state.renderCanvas) {
      this.renderCanvas();
      this.setState({ renderCanvas: false });
    }
  }

  /**
   * Control the canvas re-render
   */
  componentWillReceiveProps(nextProps) {
    if(this.props.width != nextProps.width ||
       this.props.height != nextProps.height ||
       this.props.scale != nextProps.scale) {
      this.setState({ renderCanvas: true });
    }
  }

  /**
   * Is fired when component is removed from page
   */
  componentWillUnmount() {
    this.mounted = false;
    this.page = null;
    this.pdf = null;
    this.textContent = null;
  }

  /**
   * Is fired when given page is loaded
   */
  renderCanvas() {
    if(!this.isLoaded()) return;

    var viewport = this.page.getViewport(this.props.scale);
    var canvas = ReactDOM.findDOMNode(this.refs.canvas);
    var context = canvas.getContext("2d");

    this.page.render({
      canvasContext: context,
      viewport: viewport
    });
  }

  /**
   * Renders the text layer. Returns a set of HTML nodes for React.
   * TODO We could move this to a separate component
   */
  renderTextLayer() {
    if(!this.isLoaded()) return;

    // return the highlighted terms
    var words = [],
        index = 0;

    // walk over text components
    for(let item of this.textContent.items) {
      // does highlighted term match?
      if(this.isHighlightedWord(this.props.highlightedTerms, item.str)) {
        if(this.page.pageInfo.rotate == 90) {
          let x = parseInt(item.transform[5] * this.props.scale),
              y = parseInt(item.transform[4] * this.props.scale),
              width = parseInt(item.width * this.props.scale),
              height = parseInt(item.height * this.props.scale);
               
              height = this.props.scale * Math.sqrt(item.transform[2]*item.transform[2] + item.transform[3]*item.transform[3]); // see https://github.com/mozilla/pdf.js/issues/8276
        
          words.push(
            <div className="document-viewer-page-textlayer-word"
                key={`highlight-${index}`}
                style={{ left: x, top: y - height, width: width, height: height}} />
          );
        } else {
          let x = parseInt(item.transform[4] * this.props.scale),
              y = parseInt(item.transform[5] * this.props.scale),
              width = parseInt(item.width * this.props.scale),
              height = parseInt(item.height * this.props.scale);
      
              height = this.props.scale * Math.sqrt(item.transform[2]*item.transform[2] + item.transform[3]*item.transform[3]); // see https://github.com/mozilla/pdf.js/issues/8276
    
          words.push(
            <div className="document-viewer-page-textlayer-word"
                key={`highlight-${index}`}
                style={{ left: x, bottom: y, width: width, height: height}} />
          );
        }
      }

      index++;
    }

    return words;
  }

  /**
   * Checks substring match in given highlighted words
   */
  isHighlightedWord(highlightedTerms, word) {
    word = $.trim(word).toLowerCase();

    // no use for empty strings
    if(word == "") return false;

    for(let term of highlightedTerms) {
      if(word.indexOf(term.toLowerCase()) != -1) {
        return true;
      }
    }

    return false;
  }

  /**
   * Prevents re-renders. Queues up a render after X sec.
   */
  renderCanvasDelayed() {
    // No need to re-render when pdf is not loaded yet
    if(!this.isLoaded()) return;

    // Clear render delay
    if(this.renderTimer) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    // render every X sec. to prevent
    // CPU spikes
    this.renderTimer = window.setTimeout(
      this.renderCanvas.bind(this),
      RenderingUtils.RENDER_DELAY
    );
  }

  /**
   * Starts loading PDF
   */
  loadPdf() {
    // get the document
    PDFJS.
      getDocument(this.props.pdfUrl).
      then((pdf) => {
        if(!this.mounted) return;

        this.pdf = pdf;
        this.loadPage();
      }).
      catch((error) => {
        Utils.handleError(error);
      });
  }

  /**
   * Start loading page in PDF
   */
  loadPage() {
    if(!this.mounted || !this.pdf) return;

    // retrieve page 1 (since server-side pdf's only have 1 page)
    this.
      pdf.
      getPage(1).
      then((page) => {
        if(!this.mounted || !this.pdf) return;
        this.page = page;
        this.loadTextContent();
      }).
      catch((error) => {
        Utils.handleError(error);
      });
  }

  /**
   * Starts loading text contents from PDF
   */
  loadTextContent() {
    if(!this.mounted || !this.page) return;

    this.
      page.
      getTextContent().
      then((textContent) => {
        if(!this.mounted || !this.page) return;
        this.textContent = textContent;

        this.setState({ loaded: true, renderCanvas: true });
      }).
      catch((error) => {
        Utils.handleError(error);
      });
  }

  /**
   * Checks if PDF is ready to roll
   */
  isLoaded() {
    return this.mounted && this.pdf && this.page && this.textContent;
  }

  /**
   * Is fired when favorite icon is clicked
   */
  handleFavoriteClick(event) {
    event.preventDefault();
    this.props.onFavoriteClick(event);
  }

  render() {
    var pageClass = "document-viewer-page",
        canvas,
        textLayer,
        controlsLayer,
        debugLayer,
        download,
        share,
        style;

    if(this.isLoaded()) {
      canvas = (
        <canvas
          className="document-viewer-page-canvas"
          ref="canvas"
          width={this.props.width}
          height={this.props.height}
        />
      );

      textLayer = (
        <div className="document-viewer-page-textlayer">
          {this.renderTextLayer()}
        </div>
      );

      let favoriteClassName = "document-viewer-page-controlslayer-button";

      if(this.props.isFavorite) {
        favoriteClassName += " icon-favorite";
      } else {
        favoriteClassName += " icon-favorite-open";
      }

      download = (
        <a
          className="document-viewer-page-controlslayer-button icon-download"
          href={this.props.downloadUrl}
          target="_blank"
          download
         />
      );

      if(this.props.allowShare) {
        share = (
          <span
            className="document-viewer-page-controlslayer-button icon-share"
            data-clipboard-text={this.props.sharePath}
            ref="share"
            target="_blank"
           />
         );
      }

      controlsLayer = (
        <div className="document-viewer-page-controlslayer">
          <div className="document-viewer-page-controlslayer-current-page">
            {this.props.page}
          </div>
          <a
            className={favoriteClassName}
            href="#"
            onClick={this.handleFavoriteClick.bind(this)}
          />
         {download}
         {share}
        </div>
      );

      if(this.props.debug) {
        debugLayer = (
          <div className="document-viewer-page-debug">
            {this.props.debug}
          </div>
        );
      }
    }

    style = {
      width: this.props.width,
      height: this.props.height,
      left: this.props.x,
      top: this.props.y,
    }

    return (
      <div
        className={pageClass}
        style={style}>
        {canvas}
        {textLayer}
        {controlsLayer}
        {debugLayer}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./app/javascript/portal/document_viewer/page.jsx